import {
  Skeleton,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  IconButton
} from "@mui/material";
import { useContext, useEffect, useRef, useState } from "react";
import BrandAvatar from "../../../Component/Avatar/Avatar"
import { InfoOutlined } from "@mui/icons-material";
import ErrorView from "../../../Component/ErrorView/ErrorView";
import MUIIconButton from "../../../Component/UI/MUIIconButton/MUIIconButton";
import { metricExplainations } from "../../../utils/constant";
import { extractDomain } from "../../../utils/helpers";
import DetailedTableModal from "../BrandAuditMetricDetails";
import { formatName } from "../../Dashboard/Component/CoreAttributes/CoreAttributes";
import { DiscoveryContext } from "../discovery-context";
import MUICard from "./../../../Component/UI/MUICard/MUICard";
import Table, {
  StyledTableCell,
  StyledTableRow,
} from "./../../../Component/UI/Table/Table";
import useGeneratePreCategoryDimensions from "./../useGeneratePreCategoryDimensions";
import classes from "./CategoryDimensions.module.css";
import MUIToolTip from "../../../Component/UI/ActionTooltip/MUIToolTip";
import {
  createActionMetric,
  fetchActionMetrics,
} from "../../../redux/actions/metricActions";
import { toast } from "react-toastify";
import { AuthContext } from "../../../context/auth-context";
import { formatSourceData, formatOldSourceData } from "../../../utils/metric-sources"
import { useDispatch } from "react-redux";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import useBaseResponsesModal from "../../../Component/BaseResponseComponent/useBaseResponsesModal";
import BaseResponsesModal from "../../../Component/BaseResponseComponent/BaseResponsesModal";
import { ENTITY_LABELS } from "../../../utils/labelMapping";


export default function CategoryDimensions({ category, entity, ctx, module: moduleProp = "brandAudit" }) {
  const [showModal, setShowModal] = useState(false);
  const _defaultCtx = useContext(DiscoveryContext);
  const discoveryCtx = ctx || _defaultCtx;
  const { preDimensions } = useGeneratePreCategoryDimensions();

  const [isActionLoading, setIsActionLoading] = useState(false);
  const { user } = useContext(AuthContext);
  const dispatch = useDispatch();
  const customerId = user?.customer_id;
  const selectedLLM = discoveryCtx.state.selectedLLM;
  const selectedPersona = discoveryCtx.state.selectedPersona;
  const primaryMarket = discoveryCtx.markets[0];
  const type = discoveryCtx.type;
  const showOldSourcesRecord = discoveryCtx.state.sourceValidationStatus === "PENDING";
  const _defaultComps = discoveryCtx.competitors?.['default'] ?? [];
  const isCompanyUser = user?.user_role?.role?.name === "Company user";
  const iswebSearchLLM = selectedLLM?.toLowerCase()?.includes("search")
  let loading = true;
  let criterias = {};
  let sourcesCategoryDim = [];
  let isValidated = false;
  let hasError = false;
  const transactionStatus = discoveryCtx.state.transactionStatus
  const categoryDimension = discoveryCtx?.category_dimensions?.[selectedLLM]?.[category]?.[primaryMarket]?.[selectedPersona]
  const baseResponsesData = categoryDimension?.baseResponses || {};

  const {
    infoModalOpen,
    infoModalTitle,
    infoModalContent,
    infoModalTabs,
    infoModalGlobalContent,
    closeInfoModal,
    handleCategoryDimensionClick,
  } = useBaseResponsesModal({ baseResponses: baseResponsesData }, "category-dimension");
  const TABLE_HEADERS = [
    {
      id: "criteria",
      name: ENTITY_LABELS.purchaseCriteria,
    },
  ];

  const allHeaderColumns = [
    ...TABLE_HEADERS,
    ..._defaultComps.map((item) => ({ id: item.name, name: item.name })),
    ...(discoveryCtx.competitors?.[category] ?? []).map((item) => ({ id: item.name, name: item.name })),
  ];

  const totalBrandColumns = allHeaderColumns.length - 1;

  const saveActions = async (actionStatement, explanation, sources, brand) => {
    setIsActionLoading(true);
    const modifiedObj = {
      title: actionStatement,
      rationale: explanation,
      brand,
      persona: "General Persona",
      geography: primaryMarket == "General Market" ? "General Location" : primaryMarket,
      market: "General Market",
      category,
      llm: selectedLLM,
      isProduct: type === "product" ? true : false,
      sources,
    };

    const payload = {
      action_json: [modifiedObj],
      is_archived: 0,
      is_active: 1,
    };
    dispatch(createActionMetric(customerId, payload))
      .then(() => {
        dispatch(fetchActionMetrics(customerId));
        setIsActionLoading(false);
        toast.success("Action Added!", {
          autoClose: 700,
        });
      })
      .catch(() => {
        setIsActionLoading(false);
        toast.error("Something went wrong!", {
          autoClose: 700,
        });
      });
  }

  function LoadingSkeletion() {
    return (
      <div className="p-3">
        <Skeleton variant="rectangular" height={30} />
        {Array(10)
          .fill(0)
          .map((_, index) => (
            <Skeleton
              variant="rectangular"
              width={index % 2 === 0 ? "70%" : "97%"}
              key={index}
              className="my-3"
              height={20}
            />
          ))}
      </div>
    );
  }

  function renderHeaders(criterias) {
    const categoryComps = discoveryCtx.competitors?.[category] ?? [];
    const headers = [
      ...TABLE_HEADERS,
      ..._defaultComps.map((item) => ({ id: item.name, name: item.name })),
      ...categoryComps.map((item) => ({ id: item.name, name: item.name })),
    ];

    return (
      <TableHead className={classes.table_head}>
        <TableRow>
          {headers.map((header, index) => {
            let ctxCompetitor = null;
            if (index !== 0) {
              if (index <= _defaultComps.length) {
                ctxCompetitor = _defaultComps[index - 1];
              } else {
                ctxCompetitor = categoryComps[index - 1 - _defaultComps.length];
              }
            }

            let domain = "";
            if (ctxCompetitor && ctxCompetitor?.domain) {
              domain = extractDomain(ctxCompetitor?.domain);
            }

            return (
              <TableCell key={index} className={index == 0 ? classes.table_head_cell_index : classes.table_head_cell}>
                <div className={classes.table_head_content}>
                  {index !== 0 ? (
                    <>
                      {/* Logo with Info Icon overlay */}
                      <div className={classes.logo_container}>
                        <BrandAvatar
                          domainName={domain}
                          brandName={header.name}
                        />

                        {!isCompanyUser && Object.keys(baseResponsesData)?.length > 0 && (
                          <div className={classes.info_icon_wrapper}>
                            <Tooltip title="View Base Prompt" arrow placement="top">
                              <IconButton
                                size="small"
                                onClick={() => handleCategoryDimensionClick(header.name, entity, iswebSearchLLM, baseResponsesData, { forceFilterByBrand: moduleProp === "categoryAnalysis" })}
                                sx={{
                                  padding: '1px',
                                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                  color: 'rgba(0, 0, 0, 0.54)',
                                  border: '1px solid rgba(0,0,0,0.08)',
                                  '&:hover': {
                                    backgroundColor: '#2196F3',
                                    color: '#fff',
                                    border: '1px solid #2196F3',
                                  },
                                  boxShadow: '0 1px 4px rgba(0,0,0,0.18)',
                                }}
                              >
                                <InfoOutlinedIcon sx={{ fontSize: '13px' }} />
                              </IconButton>
                            </Tooltip>
                          </div>
                        )}
                      </div>

                      {/* Brand Name below logo */}
                      <div className={classes.table_head_text}>
                        {header.name}
                      </div>
                    </>
                  ) : (
                    // First column - "Purchase Criteria"
                    <div className={classes.table_head_text_comp}>
                      <div className={classes.table_head_title}>
                        <span>{header.name}</span>
                        {!showModal && (
                          <MUIIconButton onClick={() => setShowModal(true)}>
                            <InfoOutlined />
                          </MUIIconButton>
                        )}
                      </div>
                    </div>
                  )}

                </div>
              </TableCell>
            );
          })}
        </TableRow>
      </TableHead>
    );
  }

  function renderRows(rowItem, tableIndex) {
    return (
      <StyledTableRow key={tableIndex}>
        <StyledTableCell className={classes.attribute_headings}>
          {rowItem.name}
        </StyledTableCell>
        {rowItem.brands.map((item, index) => {
          let color = "#dc3545";
          let rating = "low";
          const displayedRating = item.rating ? Number(Number(item.rating).toFixed(0)) : null;
          if (displayedRating >= 7) {
            rating = "fair";
            color = "#2196F3";
          }
          if (displayedRating >= 9) {
            color = "#198754";
            rating = "high";
          }

          const attributeSources = Array.isArray(item?.sources) && item?.sources?.length > 0 ? item?.sources : sourcesCategoryDim;
          const isEntityMatch =
            item?.name?.toLowerCase() === discoveryCtx.entity?.toLowerCase();

          const isCustomTooltip =
            isEntityMatch && iswebSearchLLM && (showOldSourcesRecord || isValidated);

          const sources = isCustomTooltip
            ? (showOldSourcesRecord
              ? formatOldSourceData(attributeSources, selectedLLM)
              : formatSourceData(attributeSources, selectedLLM))
            : {};

          const TooltipComponent = isCustomTooltip ? MUIToolTip : Tooltip;

          const tooltipProps = isCustomTooltip
            ? {
              metric: ENTITY_LABELS.purchaseCriteria,
              attribute: rowItem.name,
              metricScore: Number(item.rating).toFixed(0),
              selectedInput: {
                llm: selectedLLM,
                market: "General Market",
                category,
                brand: item.name,
                product: item.name,
                persona: "General Persona",
                geography: primaryMarket == "General Market" ? "General Location" : primaryMarket
              },
              addToActionHandler: saveActions,
              actionText: "Add To Action",
              isActionLoading: isActionLoading,
              title: item.explanation,
              arrow: true,
              isBrandAudit: true,
              sources,
              slotProps: {
                popper: {
                  modifiers: [
                    {
                      name: "offset",
                      options: {
                        offset: [0, -18],
                      },
                    },
                  ],
                },
              }
            }
            : {
              title: item.explanation,
              arrow: true,
              slotProps: {
                popper: {
                  modifiers: [
                    {
                      name: "offset",
                      options: {
                        offset: [0, -18],
                      },
                    },
                  ],
                },
              },
            };

          return (
            item.rating ? (
              <TooltipComponent {...tooltipProps}>
                <StyledTableCell
                  style={{ color }}
                  className={classes.attribute_score}
                >
                  {Number(item.rating).toFixed(0)}
                </StyledTableCell>
              </TooltipComponent>
            )
              : (
                <StyledTableCell
                  style={{ color }}
                  className={classes.attribute_score}
                >
                  -
                </StyledTableCell>
              )
          );
        })}
      </StyledTableRow>
    );
  }

  if (
    categoryDimension &&
    Object.keys(categoryDimension).length > 0
  ) {
    criterias = categoryDimension
    sourcesCategoryDim = categoryDimension?.sources
    isValidated = categoryDimension?.isValidated;
    loading = false;
  } else if (transactionStatus === "RETRY") {
    hasError = true;
    loading = false;
  } else if (transactionStatus === "RUNNING") {
    hasError = false;
    loading = true;
  } else {
    loading = false;
  }
  return (
    <MUICard variant="elevated" elevation={0} className={classes.card}>
      {!loading && !hasError && criterias?.data?.length > 0 && (
        <>
          <div className={classes.table_scroll_wrapper}>
            <div
              style={{
                minWidth: `calc(200px + ${totalBrandColumns} * 130px)`,
              }}
            >
              <Table
                elevation={0}
                data={criterias?.data}
                renderHeaders={() => renderHeaders(criterias?.data)}
                render={renderRows}
              />
            </div>
          </div>

          <DetailedTableModal
            show={showModal}
            onClose={() => setShowModal(false)}
            headers={[
              ..._defaultComps.map((item) => item.name),
              ...(discoveryCtx.competitors?.[category]?.map((item) => item.name) ?? []),
            ]}
            data={criterias?.data}
            renderHeaders={renderHeaders}
            formatName={formatName}
            classes={classes}
            pdfHeight={300}
            pdfTitle={`${entity}-${ENTITY_LABELS.purchaseCriteria}`}
          />
          <BaseResponsesModal
            open={infoModalOpen}
            title={infoModalTitle}
            content={infoModalContent}
            tabs={infoModalTabs}
            globalContent={infoModalGlobalContent}
            onClose={closeInfoModal}
          />
        </>
      )}
      {hasError && (
        <div className={classes.error}>
          <ErrorView
            title={"Error Fetching Category Dimensions"}
            message={"Request Failed"}
          />
        </div>
      )}
      {loading && !hasError && <LoadingSkeletion />}
    </MUICard>
  );
}
